import React from "react";

/**
 * Field — form field wrapper (Phase 2b Forms).
 *
 * Consolidates the label / required-marker / hint / control / error
 * slot pattern that every existing form re-implements by hand (AdminUsers,
 * UserProfile, NotFound, InputsPanel, scenarios editor — audit §3.1).
 *
 * Slot order (vertical, top → bottom):
 *   1. <label>{label}{required ? * : null}</label>
 *   2. children (the control — Input, Select, custom <textarea>, etc.)
 *   3. error  OR  hint  (mutually exclusive; error takes priority)
 *
 * ARIA:
 *   - <label htmlFor={htmlFor}> wires to the inner control's id.
 *   - error renders with role="alert" + id={htmlFor + "-error"} so a
 *     caller can set aria-describedby on the control to that id.
 *   - hint renders with id={htmlFor + "-hint"} for the same reason.
 *   - The actual wiring of aria-describedby on the control is the caller's
 *     responsibility for now (Phase 2c may add a FieldContext autowire).
 *
 * Sizing:
 *   - Vertical gap between label/control/error|hint: var(--space-1) (4px).
 *   - Label typography: --fs-body-sm, --text-muted.
 *   - Hint: --fs-caption, --text-muted.
 *   - Error: --fs-caption, --danger.
 *
 * Why a wrapper rather than props on Input: many forms have non-Input
 * controls (segmented Tab, custom drawers, checkbox groups) — keeping
 * label/error/hint in a wrapper lets every control type share it.
 */

export interface FieldProps {
  /** Visible label. Pass `null`/empty for a label-less field (rare). */
  label?: React.ReactNode;
  /** Adds * marker beside the label + sets aria-required on hint text. */
  required?: boolean;
  /** Helper text below the control. Mutually exclusive with error. */
  hint?: React.ReactNode;
  /** Error message below the control. Wins over hint when both are set. */
  error?: React.ReactNode;
  /** id of the inner control — wired to <label for=...>. Falls back to useId. */
  htmlFor?: string;
  /** Dims label + hint (the control itself manages its own disabled state). */
  disabled?: boolean;
  /** The control. Single child element is the typical shape. */
  children: React.ReactNode;
  /** Extra classes on the field wrapper. Rare; prefer Form layout helpers. */
  className?: string;
  /** Inline styles on the field wrapper. Use sparingly — for one-off
      flex/grid positioning when a form mixes a Field with another inline
      element (e.g. the AdminUsers unlock micro-form puts a flex-1 Field
      next to a submit button on the same row). */
  style?: React.CSSProperties;
}

export default function Field({
  label,
  required = false,
  hint,
  error,
  htmlFor,
  disabled = false,
  className = "",
  style,
  children,
}: FieldProps) {
  // Auto-generate an id if the caller didn't provide one. This is for the
  // hint/error region ids; the inner control still needs to receive
  // htmlFor as its `id` for the <label for=...> wiring to be useful.
  const reactId = (React as any).useId ? (React as any).useId() : null;
  const fieldId = htmlFor || reactId || undefined;
  const hasError = error != null && error !== false && error !== "";
  const hasHint = !hasError && hint != null && hint !== false && hint !== "";

  return (
    <div
      className={[
        "v2-field",
        disabled ? "v2-field--disabled" : "",
        hasError ? "v2-field--invalid" : "",
        className,
      ].filter(Boolean).join(" ")}
      style={style}
    >
      {label != null && label !== "" ? (
        <label className="v2-field__label" htmlFor={fieldId}>
          {label}
          {required ? (
            <span className="v2-field__required" aria-hidden="true"> *</span>
          ) : null}
        </label>
      ) : null}
      <div className="v2-field__control">{children}</div>
      {hasError ? (
        <div
          id={fieldId ? `${fieldId}-error` : undefined}
          className="v2-field__error"
          role="alert"
        >
          {error}
        </div>
      ) : null}
      {hasHint ? (
        <div
          id={fieldId ? `${fieldId}-hint` : undefined}
          className="v2-field__hint"
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}
